import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
// useConfirm/useAlert, never window.confirm/alert: the globals resolve silently
// (no-undef can't catch them) but bypass the app's dialog component.
import { useConfirm, useAlert } from '../components/ConfirmModal';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/shared/Navbar';
import API from '../utils/api';
import SEO from '../components/SEO';
import { trackCommunityLike, trackCommunityView, trackCommunityStoryReadAloud } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import LeadEmailForm from '../components/LeadEmailForm';
import safeStorage from '../utils/safeStorage';

const SPEECH_LANG = {
  en:'en-GB', es:'es-ES', fr:'fr-FR', hi:'hi-IN',
  gu:'gu-IN', de:'de-DE', pt:'pt-BR', ar:'ar-SA',
  zh:'zh-CN', ja:'ja-JP', it:'it-IT',
};

const LANG_FLAGS = { en:'🇬🇧', es:'🇪🇸', fr:'🇫🇷', hi:'🇮🇳', gu:'🇮🇳', de:'🇩🇪', pt:'🇧🇷', ar:'🇸🇦', zh:'🇨🇳', ja:'🇯🇵', it:'🇮🇹' }

// iOS Safari's speechSynthesis.pause() is broken — use cancel+restart at last position instead.
const isIOSSpeech = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));;
const GENRE_ICONS = {
  magical:'✨', adventure:'🗺️', adventurous:'🗺️', funny:'😄', calm:'🌙',
  educational:'📚', cozy:'🔥', exciting:'⚡', spooky:'👻', fantasy:'🧙',
  mystery:'🔍', nature:'🌿', science:'🔬', bedtime:'🛌',
};

const CommunityStoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const confirm  = useConfirm();
  const alert    = useAlert();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  // Community safety (report / block) — see communitySafetyController.js
  const [showReport, setShowReport]       = useState(false);
  const [reportReason, setReportReason]   = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting]         = useState(false);
  const [reported, setReported]           = useState(false);
  const [reportMsg, setReportMsg]         = useState('');
  const [blocking, setBlocking]           = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [speaking, setSpeaking] = useState(false);
  const [speechPaused, setSpeechPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeParagraph, setActiveParagraph] = useState(-1);
  const [speechError, setSpeechError] = useState(null);
  const utteranceRef = useRef(null);
  const resumeIntervalRef = useRef(null);
  const voicesRef = useRef([]);
  const speechParaPosRef = useRef([]);
  const speechLastCharRef = useRef(0);
  const speechTextOffsetRef = useRef(0);
  const speechFullTextRef = useRef('');

  useEffect(() => {
    // Restore liked state from localStorage (persists across page reloads)
    if (safeStorage.getItem(`community_liked_${slug}`)) {
      setLiked(true);
    }

    API.get(`/stories/community/${slug}`)
      .then(r => {
        setStory(r.data.story);
        setLikes(r.data.story.communityLikes || 0);
        trackCommunityView(slug);

        // Count view only once per browser session per story
        const key = `viewed_community_${slug}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          API.post(`/stories/community/${slug}/view`).catch(() => {});
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const isOwnStory = user && story && story.parent && story.parent.toString() === user._id?.toString();

  // Pre-load voices on mount (mobile requirement)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const stopSpeech = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
    setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
    speechLastCharRef.current = 0;
    speechTextOffsetRef.current = 0;
  };

  // Cancel speech on unmount or page refresh
  useEffect(() => {
    window.addEventListener('beforeunload', stopSpeech);
    return () => { stopSpeech(); window.removeEventListener('beforeunload', stopSpeech); };
  }, []);

  const handleReadAloud = () => {
    if (!window.speechSynthesis) return;

    // ── Pause ─────────────────────────────────────────────────────────────────
    if (speaking && !speechPaused) {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      if (isIOSSpeech()) {
        window.speechSynthesis.cancel(); // iOS: pause() broken — cancel and track position
      } else {
        window.speechSynthesis.pause();
      }
      setSpeechPaused(true);
      return;
    }

    // ── Non-iOS resume ────────────────────────────────────────────────────────
    if (speaking && speechPaused && !isIOSSpeech()) {
      window.speechSynthesis.resume();
      setSpeechPaused(false);
      resumeIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      }, 10000);
      return;
    }

    // ── Fresh start OR iOS resume — both create a new utterance ───────────────
    const langCode = story.language || 'en';
    const bcp47 = SPEECH_LANG[langCode] || langCode;
    const langPrefix = bcp47.split('-')[0];

    const voices = voicesRef.current.length ? voicesRef.current : window.speechSynthesis.getVoices();
    const lesson = story.moralLesson ? ` Tonight's lesson: ${story.moralLesson}` : '';
    const fullText = (story.title + '. ' + story.content + lesson)
      .replace(/\*\*|__|\*|_/g, '').replace(/\n\n+/g, '. ')
      .replace(/\n/g, ' ').replace(/\.\.+/g, '.').replace(/\s{2,}/g, ' ')
      .replace(/\s+([.,!?])/g, '$1').trim();

    const LANG_NAMES = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
    const voice = voices.length
      ? (voices.find(v => v.lang === bcp47 && v.localService) ||
         voices.find(v => v.lang === bcp47) ||
         voices.find(v => v.lang.startsWith(langPrefix)) ||
         (langCode === 'en' ? voices.find(v => v.lang.startsWith('en')) : null))
      : null;

    if (!voice && langCode !== 'en') {
      const langName = LANG_NAMES[langCode] || langCode.toUpperCase();
      setSpeechError(`Your browser has no ${langName} voice installed. Try on a mobile device for ${langName} read-aloud.`);
      return;
    }
    setSpeechError(null);

    const isFresh = !(speaking && speechPaused);
    const offset = isFresh ? 0 : speechLastCharRef.current;
    const text = offset > 0 && speechFullTextRef.current ? speechFullTextRef.current.slice(offset) : fullText;

    if (isFresh) {
      speechFullTextRef.current = fullText;
      speechLastCharRef.current = 0;
      speechTextOffsetRef.current = 0;
      const paragraphs = (story.content || '').split('\n\n');
      const titlePart = story.title + '. ';
      let pos = titlePart.length;
      speechParaPosRef.current = paragraphs.map(p => {
        const start = pos; pos += p.replace(/\*\*|__|\*|_/g, '').length + 2; return start;
      });
      trackCommunityStoryReadAloud(story.language || 'en');
    }
    speechTextOffsetRef.current = offset;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = bcp47; utterance.rate = playbackSpeed * 0.85; utterance.pitch = 1.05;
    if (voice) utterance.voice = voice;

    utterance.onboundary = (e) => {
      if (e.name !== 'word' && e.name !== 'sentence') return;
      const abs = e.charIndex + speechTextOffsetRef.current;
      speechLastCharRef.current = abs;
      const pp = speechParaPosRef.current;
      let active = -1;
      for (let i = 0; i < pp.length; i++) { if (abs >= pp[i]) active = i; else break; }
      setActiveParagraph(active);
    };
    utterance.onend = () => {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      speechLastCharRef.current = 0; speechTextOffsetRef.current = 0;
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') {
        if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
        setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      }
    };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    resumeIntervalRef.current = setInterval(() => {
      if (window.speechSynthesis.paused && !speechPaused) window.speechSynthesis.resume();
    }, 10000);
    setSpeaking(true); setSpeechPaused(false);
  };

  const handleReport = async () => {
    setReporting(true); setReportMsg('');
    try {
      const r = await API.post(`/stories/community/${slug}/report`, {
        reason: reportReason,
        details: reportDetails,
      });
      setReported(true);
      setReportMsg(r.data?.message || 'Thanks — our team will review this story.');
      setTimeout(() => setShowReport(false), 2500);
    } catch (err) {
      setReportMsg(err.response?.data?.message || 'Could not send the report. Please try again.');
    } finally {
      setReporting(false);
    }
  };

  const handleBlockAuthor = async () => {
    const ok = await confirm({
      title: 'Block this author?',
      message: "You won't see any stories from this author in the community again.",
      confirmLabel: 'Block',
      variant: 'danger',
    });
    if (!ok) return;
    setBlocking(true);
    try {
      await API.post(`/stories/community/${slug}/block`);
      // Their stories are now filtered server-side, so this one is gone too.
      navigate('/community');
    } catch (err) {
      await alert({
        title: 'Could not block',
        message: err.response?.data?.message || 'Please try again.',
        danger: true,
      });
    } finally {
      setBlocking(false);
    }
  };

  const handleLike = async () => {
    if (liked || isOwnStory) return;
    try {
      const r = await API.post(`/stories/community/${slug}/like`);
      setLikes(r.data.communityLikes);
      setLiked(true);
      safeStorage.setItem(`community_liked_${slug}`, '1');
      trackCommunityLike();
    } catch (err) {
      // If backend says already liked (e.g. different tab / cleared state), sync UI
      if (err.response?.data?.alreadyLiked) {
        setLiked(true);
        safeStorage.setItem(`community_liked_${slug}`, '1');
      }
    }
  };

  if (loading) return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title="Bedtime Story — Dreamy Bedtime"
        description="A personalised bedtime story on Dreamy Bedtime."
        canonical={`/community/${slug}`}
      />
      <Navbar />
      <div className="text-center py-5 mt-5">
        <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
      </div>
    </div>
  );

  if (notFound || !story) return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO title="Story Not Found" description="" noIndex={true} />
      <Navbar />
      <div className="container text-center py-5 mt-5">
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📚</div>
        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Story not found</h4>
        <p className="text-muted mb-4">This story may have been removed or the link is incorrect.</p>
        <Link to="/community" className="btn btn-dream rounded-pill px-5">Browse Community</Link>
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title={story.title}
        description={story.summary || `A personalised bedtime story: "${story.title}". Read and share magical personalised stories on Dreamy Bedtime.`}
        canonical={`/community/${story.publishSlug}`}
        ogType="article"
        ogImage={story.illustrationUrl || story.coverImage || undefined}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: story.title,
          description: story.summary || `A personalised bedtime story: "${story.title}"`,
          image: story.illustrationUrl || story.coverImage || 'https://dreamybedtime.com/og-image.png',
          url: `https://dreamybedtime.com/community/${story.publishSlug}`,
          datePublished: story.publishedAt || story.createdAt,
          dateModified:  story.publishedAt || story.createdAt,
          author: { '@type': 'Organization', name: 'Dreamy Bedtime', url: 'https://dreamybedtime.com' },
          publisher: {
            '@type': 'Organization',
            name: 'Dreamy Bedtime',
            url: 'https://dreamybedtime.com',
            logo: { '@type': 'ImageObject', url: 'https://dreamybedtime.com/icon-192.png' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://dreamybedtime.com/community/${story.publishSlug}` },
          genre: story.genre || 'children\'s story',
          audience: { '@type': 'Audience', audienceType: 'children' },
          keywords: [story.genre, story.learningTopic, story.moralLesson, 'bedtime story', 'children\'s story'].filter(Boolean).join(', '),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dreamybedtime.com' },
            { '@type': 'ListItem', position: 2, name: 'Community Stories', item: 'https://dreamybedtime.com/community' },
            { '@type': 'ListItem', position: 3, name: story.title, item: `https://dreamybedtime.com/community/${story.publishSlug}` },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      {/* Hero bar */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)', padding: '32px 0 24px' }}>
        <div className="container">
          <Link to="/community" className="btn btn-sm btn-outline-light rounded-pill mb-3">
            <i className="bi bi-arrow-left me-1"></i>Back to Community
          </Link>
          <h1 className="fw-bold text-white mb-1" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(22px,4vw,36px)' }}>
            {story.title}
          </h1>
          <div className="d-flex align-items-center gap-3 flex-wrap mt-2" style={{ color: '#c9a8ff', fontSize: '14px' }}>
            <span><i className="bi bi-person-heart me-1"></i>{story.publishedChildName}{story.ageGroup && ` · ages ${story.ageGroup}`}</span>
            <span><i className="bi bi-clock me-1"></i>{story.readingTime || 5} min read</span>
            <span><i className="bi bi-eye me-1"></i>{story.communityViews || 0} reads</span>
          </div>
        </div>
      </div>

      <div className="container py-4" style={{ maxWidth: '720px' }}>

        {/* Font size + like bar */}
        <div className="card border-0 shadow-sm mb-4 p-3 d-flex flex-row align-items-center justify-content-between gap-2" style={{ borderRadius: '14px' }}>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.max(14, f-2))} title="Smaller text">A-</button>
            <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.min(28, f+2))} title="Larger text">A+</button>
            <button
              className="btn btn-sm rounded-pill px-3 fw-semibold"
              onClick={handleReadAloud}
              title={speaking && !speechPaused ? 'Pause' : speechPaused ? 'Resume' : 'Read aloud'}
              style={speaking && !speechPaused ? { background: '#f59e0b', color: '#fff', border: 'none' }
                : speechPaused ? { background: '#fff8ed', color: '#d97706', border: '1px solid #fcd34d' }
                : { background: 'var(--purple-section)', color: '#6f42c1', border: 'none' }}
            >
              <i className={`bi ${speaking && !speechPaused ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
              {speaking && !speechPaused ? 'Pause' : speechPaused ? 'Resume' : 'Read Aloud'}
            </button>
            {speaking && (
              <button className="btn btn-sm btn-outline-danger rounded-pill px-3 fw-semibold" onClick={stopSpeech}>
                <i className="bi bi-stop-fill me-1"></i>Stop
              </button>
            )}
          </div>

          {speechError && (
            <div className="d-flex align-items-center gap-2 p-2 mt-2 rounded-3" style={{ background: '#fff8ed', border: '1px solid #fcd34d', fontSize: '12px', color: '#92400e' }}>
              <i className="bi bi-info-circle-fill" style={{ flexShrink: 0 }}></i>
              <span style={{ flex: 1 }}>{speechError}</span>
              <button onClick={() => setSpeechError(null)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', cursor: 'pointer', flexShrink: 0 }}>×</button>
            </div>
          )}

          <div className="d-flex gap-2 flex-wrap">
            {/* Badges */}
            {story.genre && (
              <span className="badge rounded-pill" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '12px' }}>
                {GENRE_ICONS[story.genre] || '📖'} {story.genre.charAt(0).toUpperCase() + story.genre.slice(1)}
              </span>
            )}
            {story.language && story.language !== 'en' && (
              <span className="badge rounded-pill" style={{ background: '#fff3cd', color: '#856404', fontSize: '12px' }}>
                {LANG_FLAGS[story.language] || '🌐'} {story.language.toUpperCase()}
              </span>
            )}
            {story.learningTopic && (
              <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', fontSize: '12px' }}>
                🎓 {story.learningTopic}
              </span>
            )}
            {story.isMultiChild && (
              <span className="badge rounded-pill" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '12px' }}>
                👨‍👩‍👧‍👦 Family Story
              </span>
            )}
            {story.isFeatured && (
              <span className="badge rounded-pill" style={{ background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', color: 'white', fontSize: '12px' }}>
                ⭐ Featured
              </span>
            )}
          </div>

          <button
            className={`btn btn-sm rounded-pill px-3 fw-semibold flex-shrink-0 ${liked ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={handleLike}
            disabled={liked || isOwnStory}
            title={isOwnStory ? "You can't like your own story" : undefined}
            style={isOwnStory ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          >
            <i className={`bi ${liked ? 'bi-heart-fill' : 'bi-heart'} me-1`}></i>
            {likes} {likes === 1 ? 'like' : 'likes'}
          </button>
        </div>

        {/* Report / block — required for user-generated content (App Store
            guideline 1.2 / Play UGC policy). Hidden on your own story. */}
        {user && !isOwnStory && (
          <div className="d-flex justify-content-end gap-2 mb-3">
            <button className="btn btn-sm btn-link text-muted text-decoration-none p-0 me-2"
              onClick={() => setShowReport(true)} disabled={reported}>
              <i className="bi bi-flag me-1"></i>{reported ? 'Reported' : 'Report'}
            </button>
            <button className="btn btn-sm btn-link text-muted text-decoration-none p-0"
              onClick={handleBlockAuthor} disabled={blocking}>
              <i className="bi bi-slash-circle me-1"></i>Block author
            </button>
          </div>
        )}

        {showReport && (
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="p-3 p-lg-4">
              <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Report this story</h6>
              <p className="small text-muted">Tell us what's wrong and our team will review it.</p>
              <select className="form-select form-select-sm mb-2" value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}>
                <option value="inappropriate">Not appropriate for children</option>
                <option value="scary">Too scary / frightening</option>
                <option value="violent">Violent</option>
                <option value="sexual">Sexual content</option>
                <option value="hateful">Hateful or abusive</option>
                <option value="personal_info">Contains personal information</option>
                <option value="spam">Spam</option>
                <option value="other">Something else</option>
              </select>
              <textarea className="form-control form-control-sm mb-2" rows="2" maxLength={1000}
                placeholder="Any extra detail (optional)" value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)} />
              {reportMsg && <div className="small mb-2" style={{ color: 'var(--text-green)' }}>{reportMsg}</div>}
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-danger rounded-pill px-3" onClick={handleReport} disabled={reporting}>
                  {reporting ? 'Sending…' : 'Submit report'}
                </button>
                <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                  onClick={() => setShowReport(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Story card */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
          <div className="p-2" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}></div>

          <div className="p-4 p-lg-5">

            {/* Illustration */}
            {story.illustrationUrl && (
              <div className="mx-auto mb-5" style={{ maxWidth: '480px' }}>
                <img
                  src={story.illustrationUrl}
                  alt={story.title}
                  style={{ width: '100%', borderRadius: '16px', display: 'block', boxShadow: '0 8px 32px rgba(111,66,193,0.18)' }}
                />
              </div>
            )}

            {/* Speed controls — shown while narration is active */}
            {(speaking || speechPaused) && (
              <div className="d-flex align-items-center gap-1 mb-3">
                <span style={{fontSize:'12px', color:'var(--text-soft)', marginRight:'4px'}}>Speed:</span>
                {[0.75, 1, 1.25].map(s => (
                  <button key={s} onClick={() => setPlaybackSpeed(s)}
                    className="btn btn-sm rounded-pill px-2 py-0"
                    style={{
                      fontSize:'12px', lineHeight:'1.7',
                      background: playbackSpeed === s ? '#6f42c1' : 'transparent',
                      color: playbackSpeed === s ? '#fff' : '#6f42c1',
                      border: `1px solid ${playbackSpeed === s ? 'transparent' : '#c9a8ff'}`,
                    }}>
                    {s}x
                  </button>
                ))}
              </div>
            )}
            {/* Story text */}
            <div style={{ lineHeight: '1.9', fontSize: `${fontSize}px`, color: 'var(--text-main)' }}>
              {story.content?.split('\n\n').map((para, i) => {
                const isActive = activeParagraph === i && (speaking || speechPaused);
                return (
                  <p key={i} className="mb-4" style={isActive ? {
                    background: 'rgba(111,66,193,0.09)',
                    borderLeft: '3px solid #6f42c1',
                    paddingLeft: '10px',
                    borderRadius: '4px',
                    transition: 'background 0.3s',
                  } : { paddingLeft: 0, borderLeft: 'none' }}>
                    {para}
                  </p>
                );
              })}
            </div>

            {/* Moral lesson */}
            {story.moralLesson && (
              <div className="mt-5 p-4 text-center rounded-3" style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '2px dashed #c9a8ff' }}>
                <div className="fs-2 mb-2">💫</div>
                <p className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Tonight's lesson</p>
                <p className="mb-0 fst-italic" style={{ color: '#6f42c1', fontSize: '17px' }}>"{story.moralLesson}"</p>
              </div>
            )}
          </div>

          <div className="p-2" style={{ background: 'linear-gradient(135deg,#6f42c1,#2d1b69)' }}></div>
        </div>

        {/* Like again at bottom */}
        <div className="text-center mt-4 mb-2">
          <button
            className={`btn rounded-pill px-5 fw-semibold ${liked ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={handleLike}
            disabled={liked || isOwnStory}
            title={isOwnStory ? "You can't like your own story" : undefined}
            style={isOwnStory ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          >
            <i className={`bi ${liked ? 'bi-heart-fill' : 'bi-heart'} me-2`}></i>
            {isOwnStory ? 'Your story' : liked ? 'You liked this story!' : 'Like this story'}
          </button>
          <div className="text-muted small mt-2">{likes} {likes === 1 ? 'family' : 'families'} loved this</div>
        </div>

        {/* Email-me-this-story lead capture (guests only) */}
        {!user && (
          <div className="card border-0 shadow-sm mt-5 p-4 p-lg-5 text-center" style={{ borderRadius: '24px', background: 'var(--card-bg)', border: '1.5px solid var(--border-purple)' }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>📬</div>
            <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
              Email me tonight's story
            </h5>
            <p style={{ color: 'var(--text-muted2)', fontSize: '14px', maxWidth: '440px', margin: '0 auto 18px' }}>
              We'll send you this exact story — personalised with <strong style={{ color: 'var(--text-main)' }}>your child</strong> as the hero instead.
            </p>
            <LeadEmailForm source="community_story" slug={slug} askName requireName buttonLabel="📬 Email my version" />
          </div>
        )}

        {/* CTA */}
        <div className="card border-0 shadow-sm mt-5 p-4 p-lg-5 text-center" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🌙</div>
          <h5 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
            Generate a story just like this
          </h5>
          <p style={{ color: '#c9a8ff', fontSize: '15px', maxWidth: '400px', margin: '0 auto 20px' }}>
            Create personalised bedtime stories for your children in minutes. Free to start.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold px-5">
              Start Free
            </Link>
            <Link to="/community" className="btn btn-outline-light rounded-pill px-4">
              Browse More Stories
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CommunityStoryPage;
